import type { IResponseBase } from '@/interfaces/IResponseBase';
import type { IGridifyPaging } from '@/interfaces/IGridifyPaging';
import type { IGridifyQuery } from '@/models/GridifyQuery';
import { Http } from './http';

const http = Http.create();

export type TJActionBehavior = 'Pendenciar' | 'Reprovar';

export interface TJActionTypeValue {
	minValidityMinutes: number;
	amount: number;
	behavior: TJActionBehavior;
}

export interface ParameterItem<TValue = unknown> {
	id?: string;
	key: string;
	category: string | number;
	type: 'Json' | number;
	value: TValue;
	scope?: string | null;
	entityType?: string | null;
	entityId?: string | null;
	createdAt?: string | Date;
	updatedAt?: string | Date;
}

export interface ParametersListQuery {
	scope?: string | null;
	entityType?: string | null;
	entityId?: string | null;
}

export class ParametersService {
	static async list<TValue = unknown>(category: string, query: ParametersListQuery = {}) {
		const response = await http.get<IResponseBase<ParameterItem<TValue>[]>>(`/Parameters`, {
			params: { category, ...query },
		});
		return response.data;
	}

	static async get<TValue = unknown>(
		category: string,
		key: string,
		query: ParametersListQuery = {},
	) {
		const response = await http.get<IResponseBase<ParameterItem<TValue>>>(
			`/Parameters/${encodeURIComponent(category)}/${encodeURIComponent(key)}`,
			{ params: { ...query } },
		);
		return response.data;
	}

	static async create<TValue = unknown>(item: ParameterItem<TValue>) {
		const payload: ParameterItem<TValue> = {
			key: item.key,
			category: item.category,
			type: item.type,
			value: item.value,
			scope: item.scope ?? null,
			entityType: item.entityType ?? null,
			entityId: item.entityId ?? null,
		};

		const response = await http.post<IResponseBase<ParameterItem<TValue>>>(`/Parameters`, payload);
		return response.data;
	}

	static async update<TValue = unknown>(item: ParameterItem<TValue>) {
        if (!item.id) throw new Error('ID é obrigatório para atualização.');

        const payload: ParameterItem<TValue> = {
            id: item.id,
            key: item.key,
            category: item.category,
            type: item.type,
            value: item.value,
            scope: item.scope ?? null,
            entityType: item.entityType ?? null,
            entityId: item.entityId ?? null,
        };

        const response = await http.put<IResponseBase<ParameterItem<TValue>>>(`/Parameters`, payload);
        return response.data;
    }
	
	static async delete(id: string) {
        const response = await http.delete<IResponseBase<boolean>>(`/Parameters/${id}`);
        return response.data;
    }

	static async listTJActionTypes(query: ParametersListQuery = {}) {
		const response = await http.get<IResponseBase<ParameterItem<TJActionTypeValue>[]>>(
			`/Parameters`,
			{
				params: { category: 'TJActionType', ...query },
			},
		);
		return response.data;
	}

	static async getTJActionType(key: string, query: ParametersListQuery = {}) {
		const response = await http.get<IResponseBase<ParameterItem<TJActionTypeValue>>>(
			`/Parameters/TJActionType/${encodeURIComponent(key)}`,
			{ params: { ...query } },
		);
		return response.data;
	}

	static async createTJActionType(item: ParameterItem<TJActionTypeValue>) {
		const payload: ParameterItem<TJActionTypeValue> = {
			key: item.key,
			category: 'TJActionType',
			type: 'Json',
			value: item.value,
			scope: item.scope ?? null,
			entityType: item.entityType ?? null,
			entityId: item.entityId ?? null,
		};

		const response = await http.post<IResponseBase<ParameterItem<TJActionTypeValue>>>(
			`/Parameters`,
			payload,
		);
		return response.data;
	}

	static async updateTJActionType(item: ParameterItem<TJActionTypeValue>) {
		if (!item.id) throw new Error('ID é obrigatório.');
		return this.update(item);
   	}

   	static async deleteTJActionType(id: string) {
		return this.delete(id);
	}

	static async gridify<TValue = unknown>(query: IGridifyQuery, subject?: string) {
		const response = await http.get<IGridifyPaging<ParameterItem<TValue>>>(
			`/parameters/list`,
			{
				params: { ...query, subject },
			},
		);
		return response.data;
	}
}

export default ParametersService;
